import {Button} from '@/components/ui/button';
import {Input, Label} from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {patchEmployeeChangePassword} from '@accumulus/shared';
import {Eye, EyeOff} from 'lucide-react';
import {useEffect, useState} from 'react';
import {toast} from 'sonner';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ChangePasswordDialog = ({open, onOpenChange}: Props) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Fields stay read-only until focused so the browser cannot autofill a saved email/password on open
  const [unlockedFields, setUnlockedFields] = useState<string[]>([]);

  const resetForm = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setUnlockedFields([]);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const onSubmit = async () => {
    if (!oldPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword) {
      toast.error('Please enter your new password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword === oldPassword) {
      toast.error('New password must be different from the current password');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('The two passwords do not match');
      return;
    }

    setIsSaving(true);
    try {
      const res = await patchEmployeeChangePassword(oldPassword, newPassword);
      if (res.error) {
        toast.error(res.error_message || 'Failed to change password');
        return;
      }
      toast.success('Password changed successfully');
      resetForm();
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const passwordField = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    show: boolean,
    onToggleShow: () => void,
    placeholder: string,
    autoComplete: string
  ) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
          className="pr-10"
          name={`change-password-${label.toLowerCase().replace(/\s+/g, '-')}`}
          autoComplete={autoComplete}
          readOnly={!unlockedFields.includes(label)}
          onFocus={() =>
            setUnlockedFields((prev) =>
              prev.includes(label) ? prev : [...prev, label]
            )
          }
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          disabled={isSaving}
        />
        <button
          type="button"
          aria-label={show ? `Hide ${label}` : `Show ${label}`}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none"
          onClick={onToggleShow}
          tabIndex={-1}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {passwordField(
            'Current Password',
            oldPassword,
            setOldPassword,
            showOldPassword,
            () => setShowOldPassword((prev) => !prev),
            'Current Password',
            // "off" is ignored by browsers for password inputs; "new-password" stops saved-password autofill
            'new-password'
          )}
          {passwordField(
            'New Password',
            newPassword,
            setNewPassword,
            showNewPassword,
            () => setShowNewPassword((prev) => !prev),
            'New Password',
            'new-password'
          )}
          {passwordField(
            'Confirm New Password',
            confirmPassword,
            setConfirmPassword,
            showConfirmPassword,
            () => setShowConfirmPassword((prev) => !prev),
            'Confirm New Password',
            'new-password'
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSaving} isLoading={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
